import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Dropdown, SalvProgressCircle } from '@salvapp/ui-components';
import './AlertFilters.scss';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { Popover } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import {
  InternalApiAlertFilter,
  InternalApiAlertFilterExpressionFieldTypeEnum,
  InternalApiCustomList,
  InternalApiEntityType,
  InternalApiScenarioOption,
} from '../../api-internal';
import { AppState } from '../../store/store';
import { AlertFilterField, AlertFilterObjects, getFilterObjects } from './helper';
import { customListApi, scenarioInternalApi, universalDocumentsApi } from '../../apis';
import { fetchCustomPersonSchema } from '../../schema/schemaActions';
import { fetchAlertStatuses } from '../../screening/alert-status/alertStatusesActions';
import { CAN_DELETE_SCREENING_ALERT_FILTER } from '../../auth/permissions';
import RmsPopover from '../../common/popover/RmsPopover';
import Has from '../../auth/Has';
import AlertFiltersModal from './AlertFiltersModal';
import { useQueryUsersBasic } from '../../users/userActions';
import AlertFilterResults from './AlertFilterResults';
import { fetchAlertFilters } from './alertFiltersActions';
import isEqual from 'lodash.isequal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import Tracker from '../../tracking/tracker';
import {
  downloadDocument as downloadDocumentHelper,
  filenameFromContentDisposition,
} from '../../common/document/helper';
import { showFeedback } from '../../feedback/feedbackReducer';
import { useQueryEntitySchema } from '../../schema/entitySchemaActions';
import { useMe } from '../../organisation/useMe';
import SalvPage from '../../common/SalvPage';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { useQueryConversationCategories } from '../../bridge/bridgeActions';

export const AlertFilters = (): ReactElement | null => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { features, currentOrganisation } = useMe();
  const searchParams = new URLSearchParams(location.search);
  const [customLists, setCustomLists] = useState<InternalApiCustomList[]>([]);
  const [refreshFilters, setRefreshFilters] = useState(false);
  const [scenarios, setScenarios] = useState<InternalApiScenarioOption[]>([]);
  const { filters, loadingAlertFilters, latestUpdatedFilter } = useSelector(
    (state: AppState) => state.alertFilters,
    shallowEqual
  );
  const { customPersonSchema } = useSelector((state: AppState) => state.schema, shallowEqual);
  const { data: personSchema } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const { data: transactionSchema } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);
  const screeningEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features);
  const monitoringEnabled = isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features);
  const bridgeEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE, features);
  const bridgeAlertsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);
  const adverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);

  const [alertFiltersObjects, setAlertFiltersObjects] = useState<AlertFilterObjects>(
    getFilterObjects({
      users: [],
      statuses: [],
      personSchema: undefined,
      transactionSchema: undefined,
      screeningEnabled,
      monitoringEnabled,
      bridgeAlertsEnabled,
      bridgeEnabled,
      adverseMediaEntitiesEnabled,
    })
  );

  const statuses = useSelector((state: AppState) => state.screeningAlertStatuses.statuses, shallowEqual);
  const [selectedFilter, setSelectedFilter] = useState<InternalApiAlertFilter | undefined>();
  const { data: users } = useQueryUsersBasic();

  const downloadDocument = useCallback(async (id: number) => {
    const document = await universalDocumentsApi.downloadUniversalDocument({
      documentId: id,
    });
    const filename = filenameFromContentDisposition(document.headers, `export-${id}.csv`);
    downloadDocumentHelper(document.body, filename);
  }, []);

  const updatedQueryString = (params: URLSearchParams): string => {
    params.delete('export-id');
    return Array.from(params).length ? `?${params.toString()}` : ``;
  };

  const exportId = searchParams.get('export-id');
  useEffect(() => {
    if (exportId) {
      dispatch(showFeedback({ message: `Downloading exported file...`, variant: 'success' }));
      downloadDocument(Number(exportId));
      navigate({ search: updatedQueryString(searchParams) });
    }
  }, []);

  const { data: categories } = useQueryConversationCategories(bridgeAlertsEnabled);

  useEffect(() => {
    if (features.length === 0) return;
    const results = getFilterObjects({
      users,
      statuses,
      screeningEnabled,
      monitoringEnabled,
      personSchema,
      transactionSchema,
      customPersonSchema,
      customLists,
      scenarios,
      categories,
      bridgeAlertsEnabled,
      bridgeEnabled,
      adverseMediaEntitiesEnabled,
    });
    setAlertFiltersObjects(results);
  }, [
    users,
    statuses,
    personSchema,
    transactionSchema,
    customPersonSchema,
    customLists,
    scenarios,
    categories,
    adverseMediaEntitiesEnabled,
    features,
  ]);

  useEffect(() => {
    if (!refreshFilters || loadingAlertFilters) return;
    dispatch(fetchAlertFilters());
    setRefreshFilters(false);
  }, [refreshFilters, loadingAlertFilters]);

  useEffect(() => {
    const fetchLists = async () => {
      const lists = await customListApi.getLists();
      setCustomLists(lists);
    };
    const fetchScenarioOptions = async () => {
      const result = await scenarioInternalApi.findAllScenarioOptions();
      setScenarios(result);
    };

    fetchLists();
    fetchScenarioOptions();
  }, []);

  const getExpressionValue = (
    type: InternalApiAlertFilterExpressionFieldTypeEnum,
    value: string,
    field: AlertFilterField
  ): string => {
    switch (type) {
      case InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST:
        return JSON.parse(value)
          .map((savedValue: string) => field.options?.find((option) => option.value === savedValue)?.label)
          .join('; ');
      case InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP:
        return dayjs.unix(Number(value) / 1000).format('YYYY-MM-DD');
      default:
        return value;
    }
  };

  const advancedFilterOverlay = (
    <Popover id='advanced-filter-popover' className='advanced-filter-popover'>
      <Popover.Body>
        {selectedFilter?.expressions
          .map((expression, index) => {
            const exp = alertFiltersObjects.getObject(expression.target);
            if (!exp) {
              return;
            }
            const field = exp.getField(expression.field);
            if (!field) {
              return;
            }

            let operation: DropdownOption;
            try {
              operation = field.getOperation(expression.operation);
            } catch (e: unknown) {
              // User tried to load filter that contains NOT expression but the feature is not enabled
              if (e instanceof Error) {
                const errorMessage = `Failed to load filter <${selectedFilter?.id}> for organisation <${currentOrganisation?.id}>: ${e.message}`;
                Tracker.error(errorMessage);
              } else {
                Tracker.error(e);
              }
              return;
            }
            const value = getExpressionValue(expression.fieldType, expression.value, field);
            return (
              <div key={index} className='expression-explanation'>
                {exp.value.label} {field.value.label.toLowerCase()}{' '}
                <b>
                  {operation.label} {value}
                </b>
              </div>
            );
          })
          .filter((e) => e)}
      </Popover.Body>
    </Popover>
  );

  useEffect(() => {
    dispatch(fetchAlertFilters());
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    let newFilter: InternalApiAlertFilter | undefined = filters[0];
    if (id) {
      newFilter = filters.find((filter) => filter.id === Number(id));
    }
    // Since newFilter is an object, even if it's deeply equal to selectedFilter, react will re-render
    // the component due to different object references. As a result, we might trigger results fetch
    // multiple times. This is the reason for deep equality check
    if (!isEqual(newFilter, selectedFilter)) {
      setSelectedFilter(newFilter);
    }
  }, [searchParams, filters]);

  useEffect(() => {
    if (!customPersonSchema) {
      dispatch(fetchCustomPersonSchema());
    }
  }, [customPersonSchema, dispatch]);

  useEffect(() => {
    if (statuses && statuses.length === 0) {
      dispatch(fetchAlertStatuses());
    }
  }, [statuses, dispatch]);

  useEffect(() => {
    if (latestUpdatedFilter) {
      navigate({
        search: `?id=${latestUpdatedFilter.id}`,
      });
    }
  }, [latestUpdatedFilter]);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  return (
    <SalvPage title={'Alerts'}>
      <div className='d-flex align-items-end justify-content-between alert-filters m-b-16'>
        {selectedFilter ? (
          <Dropdown
            label='Select filter'
            value={
              selectedFilter
                ? {
                    key: selectedFilter.name,
                    label: `${selectedFilter.name} (${selectedFilter.alertCount})`,
                    value: selectedFilter.name,
                  }
                : undefined
            }
            options={filters.map((filter) => ({
              key: filter.name,
              label: `${filter.name} (${filter.alertCount})`,
              value: filter.name,
            }))}
            onChange={(option: DropdownOption[]) => {
              const filter = filters.find((filter) => filter.name === option[0].key);
              setRefreshFilters(true);
              navigate({
                search: `?id=${filter?.id}`,
              });
            }}
          />
        ) : (
          !loadingAlertFilters && <div>There are no filters available.</div>
        )}
        {selectedFilter && (
          <RmsPopover overlay={advancedFilterOverlay}>
            <span className='hover-link'>Show filter conditions</span>
          </RmsPopover>
        )}
        <Has allPermissions={CAN_DELETE_SCREENING_ALERT_FILTER}>
          <AlertFiltersModal selectedFilter={selectedFilter} alertFiltersObjects={alertFiltersObjects} />
        </Has>
      </div>

      {selectedFilter ? (
        <AlertFilterResults filter={selectedFilter} mappings={attributeMappings.map} searchParams={searchParams} />
      ) : (
        filters.length > 0 && <>Select a filter to see results</>
      )}
    </SalvPage>
  );
};

export default AlertFilters;
